import React, { useState, useEffect, useRef } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAppUpdates } from "@/lib/appUpdatesStore";
import { Sparkles } from "lucide-react";
import { usePermissions } from "@/context/PermissionContext";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, Users, LogOut, User, Pill, Stethoscope, FileText, 
  Menu, X, BarChart3, MessageSquare, Calendar, Package, Receipt, 
  ShoppingCart, Database, PackageX, ChevronDown, ChevronRight,
  Truck, UserCog, MessagesSquare, Settings, Ticket, Activity, FileEdit,
  Shield, Boxes, BookOpen, Bell, UserPlus, Star, Gift, RefreshCw, History, Upload,
  ClipboardList, AlertTriangle, Send, HelpCircle, Megaphone, Ban, AlertCircle, ClipboardCheck,
  Syringe, MapPin, Building2, CreditCard, Wallet, Award, Tag, Car, Landmark, Trash2, PackageCheck
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import MobileStickyHeader from "@/components/layout/MobileStickyHeader";
import PullToRefresh from "@/components/layout/PullToRefresh";
import PendingNakesRequestBanner from "@/components/PendingNakesRequestBanner";
import PendingOTWCancelBanner from "@/components/PendingOTWCancelBanner";
import { playNotificationSound } from "@/utils/notificationSound";
import useProductConfirmCount from '@/utils/useProductConfirmCount';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Accordion state bersama: hanya SATU grup menu yang terbuka pada satu waktu.
// Nilai = label grup yang sedang terbuka (null = semua tertutup).
const SidebarAccordionContext = React.createContext(null);

// Accordion Menu Component - with CSS hardening
function MenuCategory({ icon: Icon, label, children, defaultOpen = false, badge = 0, title }) {
  const accordion = React.useContext(SidebarAccordionContext);
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  // Kalau ada accordion bersama, pakai itu. Kalau tidak, jatuh ke state lokal (perilaku lama).
  const isOpen = accordion ? accordion.openKey === label : localOpen;
  const setIsOpen = (next) => {
    if (accordion) accordion.setOpenKey(next ? label : null);
    else setLocalOpen(next);
  };
  const location = useLocation();
  
  // Check if any child is active
  const hasActiveChild = React.Children.toArray(children).some(child => {
    if (child?.props?.to) {
      return location.pathname === child.props.to || location.pathname.startsWith(child.props.to + '/');
    }
    return false;
  });
  
  // Auto-expand if has active child - using layout effect to avoid cascading renders
  const prevHasActiveChild = useRef(hasActiveChild);
  if (hasActiveChild && !prevHasActiveChild.current) {
    // Will trigger during render, but this is intentional for initial expansion
  }
  prevHasActiveChild.current = hasActiveChild;
  
  // Expand on mount if has active child
  useEffect(() => {
    if (hasActiveChild && !isOpen) {
      if (accordion) accordion.setOpenKey((prev) => prev ?? label);
      else setLocalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <div className="mb-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={title || label}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
          hasActiveChild ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {badge > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {badge}
            </span>
          )}
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>
      {isOpen && (
        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-gray-200 pl-2">
          {children}
        </div>
      )}
    </div>
  );
}

// Submenu Item Component - with CSS hardening
function SubMenuItem({ to, icon: Icon, label, badge = 0, onClick, title }) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  
  return (
    <Link
      to={to}
      onClick={onClick}
      title={title || label}
      className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm ${
        isActive ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
        <span className="whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
      </div>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center flex-shrink-0 ml-2">
          {badge}
        </span>
      )}
    </Link>
  );
}

// Standalone Menu Item - with CSS hardening
function StandaloneMenuItem({ to, icon: Icon, label, onClick, badge = 0, title }) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  
  return (
    <Link
      to={to}
      onClick={onClick}
      title={title || label}
      className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
        isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
      </div>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center flex-shrink-0 ml-2">
          {badge}
        </span>
      )}
    </Link>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { count: appUpdatesCount } = useAppUpdates();
  const { isSuperAdmin, hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accordionOpenKey, setAccordionOpenKey] = useState(null);
  const accordionValue = React.useMemo(
    () => ({ openKey: accordionOpenKey, setOpenKey: setAccordionOpenKey }),
    [accordionOpenKey]
  );
  // Height (px) of PendingNakesRequestBanner when visible on mobile — used to
  // push MobileStickyHeader down and pad content so nothing overlaps.
  const [bannerHeight, setBannerHeight] = useState(0);
  const productConfirmCount = useProductConfirmCount(
    ['admin', 'super_admin', 'master_admin', 'supervisor', 'gudang'].includes(user?.role)
  );

  // iter386: Hide MobileStickyHeader when inside a chat ROOM (not the list).
  // Chat rooms render their own `<ChatHeaderMobile>` pinned to top:0 via
  // useVisualViewportInfo. The 56px MobileStickyHeader (z-50) would otherwise
  // overlap the chat header (z-30 root). List pages keep the navbar.
  const isInChatRoom = /^\/(admin|supervisor)\/(nakes-chats|discussions|job-discussions|coordination-channels|chat-gudang-nakes)\/[^/]+/i.test(location.pathname);

  // iter388: /admin/all-chats never changes URL when a thread opens (single-
  // page pane). Listen for the custom event AllChats.js emits so we know when
  // to hide the navbar there too.
  const [isAllChatsThreadOpen, setIsAllChatsThreadOpen] = useState(false);
  useEffect(() => {
    const onEvent = (e) => setIsAllChatsThreadOpen(!!(e.detail && e.detail.active));
    window.addEventListener('vaxa:allchats-thread-active', onEvent);
    // Route away from /all-chats → clear.
    if (!location.pathname.includes('/all-chats')) {
      setIsAllChatsThreadOpen(false);
    }
    return () => window.removeEventListener('vaxa:allchats-thread-active', onEvent);
  }, [location.pathname]);
  const hideNavbar = isInChatRoom || isAllChatsThreadOpen;
  
  // P0: Unified Badge State from badge-summary endpoint
  const [badgeSummary, setBadgeSummary] = useState({
    chat: { all_chat: 0, chat_admin: 0, chat_nakes: 0, job_discussions: 0, chat_gudang: 0, total: 0 },
    informasi: { announcements: 0, sop_updates: 0, total: 0 },
    orders: { unread_events: 0, total: 0 },
    system: { unread: 0, total: 0 },
    grand_total: 0
  });
  const prevBadgeTotalRef = useRef(null);
  
  // Admin Mandatory Tasks Badge
  const [adminTasksCount, setAdminTasksCount] = useState({
    total: 0,
    critical: 0,
    high: 0,
    medium: 0,
    normal: 0
  });

  // Papan Operasional widget count (mine_pending used as sidebar badge)
  const [papanOpsCount, setPapanOpsCount] = useState({
    mine_pending: 0,
    total_pending: 0,
    broadcasts_today: 0,
  });

  // Telegram Monitor Health Flags (super admin sidebar alert badge)
  const [tgHealthFlags, setTgHealthFlags] = useState({
    issue_count: 0,
    issues: [],
    unverified_channel_count: 0,
    super_admin_linked: 0,
  });
  
  // Marketing Operational Mode State
  const [marketingModeStatus, setMarketingModeStatus] = useState(null);
  const [togglingMode, setTogglingMode] = useState(false);
  
  // Role checks - RESTORED structure
  const userRole = user?.role || 'admin';
  const isMasterAdmin = userRole === 'master_admin';
  const isSuperAdminRole = userRole === 'super_admin' || isMasterAdmin;
  const isUserSuperAdmin = isSuperAdmin || isSuperAdminRole;
  const isSupervisor = userRole === 'supervisor';
  const isAdmin = ['admin', 'super_admin', 'master_admin', 'supervisor'].includes(userRole);
  const isMarketing = userRole === 'marketing';
  const isFinance = userRole === 'finance';
  // iter373 — Admin CRM sub-role: menu-only filter (no RBAC change).
  const isCRMAdmin = userRole === 'admin' && (user?.admin_subrole === 'CRM');

  // Supervisor visibility: shares some Super Admin menus but excludes
  // Monitoring, Pengaturan Sistem, Master Entitas, Master Jadwal Vaksin.
  const canSeeUserMgmt = isUserSuperAdmin || isSupervisor;     // Manajemen SDM
  const canSeeMasterRole = isUserSuperAdmin || isSupervisor;   // Pengaturan > Master Role
  const canSeePurchaseOrder = isUserSuperAdmin || isSupervisor; // Surat Pesanan
  
  // Helper: check if has permission
  const canDoAction = (permCode) => {
    if (isMasterAdmin) return true; // MASTER ADMIN can do EVERYTHING
    if (isUserSuperAdmin) return true;
    return hasPermission(permCode);
  };

  // Fetch Admin Tasks Count (multi-tab: admin/hrd/spv) — May 2026
  const fetchAdminTasksCount = async () => {
    // Allow admin/finance/marketing/supervisor (isAdmin) AND hrd
    if (!isAdmin && userRole !== 'hrd') return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/admin/tasks/multi-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data || {};
      const counts = data.counts || {};
      setAdminTasksCount({
        total: data.total || 0,
        critical: 0,
        high: 0,
        medium: 0,
        normal: 0,
        admin: counts.admin || 0,
        hrd: counts.hrd || 0,
        spv: counts.spv || 0,
        allowed_tabs: data.allowed_tabs || [],
      });
    } catch (error) {
      console.error('Failed to fetch admin tasks count:', error);
    }
  };

  // Fetch Papan Operasional widget count (badge for sidebar)
  const fetchPapanOpsCount = async () => {
    if (!isAdmin && userRole !== 'hrd') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/papan-operasional/widget/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPapanOpsCount(response.data);
    } catch (error) {
      // silent
    }
  };

  // Fetch Telegram Monitor Health Flags (super admin only)
  const fetchTgHealthFlags = async () => {
    if (!isUserSuperAdmin) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/telegram-monitor/health-flags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTgHealthFlags({
        issue_count: res.data.issue_count || 0,
        issues: res.data.issues || [],
        unverified_channel_count: res.data.unverified_channel_count || 0,
        super_admin_linked: res.data.super_admin_linked || 0,
      });
    } catch (e) {
      // silent
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };
  
  // P0: Fetch unified badge summary
  const fetchBadgeSummary = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/notifications/badge-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadgeSummary(response.data);
      const newTotal = response.data?.grand_total || 0;
      if (prevBadgeTotalRef.current !== null && newTotal > prevBadgeTotalRef.current) {
        playNotificationSound();
      }
      prevBadgeTotalRef.current = newTotal;
      // iter391 — PWA app icon badge (Chrome/Edge desktop + installed PWAs).
      try {
        if ('setAppBadge' in navigator) {
          if (newTotal > 0) navigator.setAppBadge(newTotal);
          else navigator.clearAppBadge?.();
        }
      } catch (_e) { /* silent */ }
    } catch (error) {
      console.error('Failed to fetch badge summary:', error);
    }
  };

  // Fetch Marketing Mode Status
  const fetchMarketingModeStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/marketing-mode/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMarketingModeStatus(response.data);
    } catch {
      // Silently handle
    }
  };

  // Toggle Marketing ALL DAY Mode (Admin only)
  const handleToggleAllDayMode = async () => {
    if (!isAdmin) return;
    
    setTogglingMode(true);
    try {
      const token = localStorage.getItem('token');
      const newEnabled = !marketingModeStatus?.all_day_enabled;
      
      await axios.post(`${API}/marketing-mode/toggle-all-day?enabled=${newEnabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchMarketingModeStatus();
    } catch (err) {
      toast.error('Gagal mengubah mode');
    } finally {
      setTogglingMode(false);
    }
  };
  
  useEffect(() => {
    // Don't fetch if no token yet. `user` may still be null while
    // AuthContext resolves /auth/me — fetch functions use localStorage token
    // directly, so we must NOT wait for `user` or the sidebar chat badge
    // will remain 0 after page reload until user hydration completes.
    const token = localStorage.getItem('token');
    if (!token) return;

    // Perf fix (Sep 2026) — pause ALL badge/count polling while the tab is
    // hidden, resume with one immediate fetch + original intervals when it
    // becomes visible again. Endpoints/payloads/handlers below are untouched;
    // only the start/stop wiring changed.
    let interval, adminTasksInterval, papanOpsInterval, tgHealthInterval, financeInterval;

    const fetchFinanceNotifications = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newUnread = response.data.unread_count || 0;
        setBadgeSummary(prev => ({
          ...prev,
          notifications: { total: newUnread }
        }));
        if (prevBadgeTotalRef.current !== null && newUnread > prevBadgeTotalRef.current) {
          playNotificationSound();
        }
        prevBadgeTotalRef.current = newUnread;
      } catch (error) {
        console.error('Failed to fetch finance notification count:', error);
      }
    };

    const startPolling = () => {
      if (!isFinance) {
        // Sep 2026 — stagger initial mount-time fetches (150ms apart) so they
        // don't all hit the server in the same tick. Polling intervals below
        // are untouched — this only affects the very first fetch on mount.
        fetchBadgeSummary();
        setTimeout(() => fetchMarketingModeStatus(), 150);
        if (isAdmin || userRole === 'hrd') {
          setTimeout(() => fetchAdminTasksCount(), 300);
        }
        if (isAdmin || userRole === 'hrd') {
          setTimeout(() => fetchPapanOpsCount(), 450);
        }
        if (isUserSuperAdmin) {
          setTimeout(() => fetchTgHealthFlags(), 600);
        }
        interval = setInterval(() => {
          fetchBadgeSummary();
          fetchMarketingModeStatus();
        }, 60000);

        // Separate interval for admin tasks
        if (isAdmin || userRole === 'hrd') {
          adminTasksInterval = setInterval(fetchAdminTasksCount, 60000);
        }

        // Papan Operasional badge — refresh every 2min (Sep 2026: card removed
        // from dashboard, no need to poll this often anymore).
        if (isAdmin || userRole === 'hrd') {
          papanOpsInterval = setInterval(fetchPapanOpsCount, 120000);
        }

        // Telegram health flags — slow poll (60s) since infra-level state
        if (isUserSuperAdmin) {
          tgHealthInterval = setInterval(fetchTgHealthFlags, 60000);
        }
      } else {
        // Finance: fetch notification count only
        fetchFinanceNotifications();
        financeInterval = setInterval(fetchFinanceNotifications, 60000);
      }
    };

    const stopPolling = () => {
      clearInterval(interval);
      clearInterval(adminTasksInterval);
      clearInterval(papanOpsInterval);
      clearInterval(tgHealthInterval);
      clearInterval(financeInterval);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isFinance, isAdmin, user]);

  // Handler for clicking Orders menu - immediately clear badge
  const handleOrdersClick = async () => {
    closeSidebar();
    // Call API to mark as seen (PER-USER)
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/notifications/orders/mark-seen`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (e) {
      // Silently fail
    }
  };

  // Total chat badge - now from badgeSummary
  const totalChatBadge = badgeSummary.chat?.total || 0;

  // Get role display name
  // May 2026 — finance/marketing/hrd users CAN reach AdminLayout via shared
  // /admin/* URLs (allowedRoles include them). Previously this fell back to
  // "Admin" for any non-admin role, making FIN002/MKT/HRD appear as Admin
  // in the sidebar. Now we return the user's actual role label.
  const getRoleLabel = () => {
    if (isMasterAdmin) return 'Master Admin';
    if (isSuperAdminRole) return 'Super Admin';
    if (isSupervisor) return 'Supervisor';
    if (isFinance) {
      const fr = (user?.finance_role || '').toUpperCase();
      if (fr === 'INVOICE') return 'Finance Invoice';
      if (fr === 'SUPPLIER') return 'Finance Supplier';
      if (fr === 'KEDUANYA') return 'Finance';
      return 'Finance';
    }
    if (isMarketing) return 'Marketing';
    if (userRole === 'hrd') return 'HRD';
    return 'Admin';
  };

  // 24 Okt 2026 — Menu "Keuangan" di sidebar Admin dihapus atas permintaan Ricy
  // (Daftar Invoice dan Surat Pesanan sudah ada di Manajemen Order / Logistik).
  // Route /admin/finance/* dan izin finance.console sengaja dibiarkan.

  return (
    <SidebarAccordionContext.Provider value={accordionValue}>
    <div className="min-h-screen bg-gray-50 lg:overflow-hidden lg:h-screen" style={{ minHeight: '100dvh' }}>
      {/* Overlay for mobile/tablet */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - FIXED on desktop (>=1024px), slide-in on mobile/tablet */}
      <div className={`
        fixed inset-y-0 left-0 z-40
        w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ height: '100dvh', maxHeight: '100dvh' }}>
        {/* Menu Items - Scrollable (sidebar header removed — branding now in MobileStickyHeader) */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto pt-14 lg:pt-4">
          {/* Marketing Operational Mode Status Badge — kept as functional admin tool */}
          {marketingModeStatus && (isAdmin || isMarketing) && (
            <div className="mb-3 p-2 rounded-lg bg-gray-50 border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    marketingModeStatus.mode === 'ALL_DAY' ? 'bg-green-500' :
                    marketingModeStatus.mode === 'AUTO_NIGHT' ? 'bg-blue-500' :
                    'bg-gray-400'
                  }`} />
                  <span className="text-xs font-medium text-gray-700 truncate">
                    {marketingModeStatus.label}
                  </span>
                </div>
                {isAdmin && (
                  <button
                    onClick={handleToggleAllDayMode}
                    disabled={togglingMode}
                    className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                      marketingModeStatus.all_day_enabled
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={marketingModeStatus.all_day_enabled ? 'Klik untuk nonaktifkan' : 'Klik untuk aktifkan ALL DAY'}
                  >
                    {togglingMode ? '...' : (marketingModeStatus.all_day_enabled ? 'ON' : 'OFF')}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {marketingModeStatus.can_nakes_ops
                  ? '✓ Marketing: Nakes ops aktif'
                  : '○ Marketing: Nakes ops via Admin'}
              </p>
            </div>
          )}
          {/* Admin CRM sub-role (iter373): menu-only filter. Only shows
              Dashboard, Manajemen Order (view/create-order stripped),
              Data Customer, Voucher, List Data Vaksin, Pusat Informasi. */}
          {isCRMAdmin ? (
            <>
              <StandaloneMenuItem to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" onClick={closeSidebar} />
              <StandaloneMenuItem
                to="/admin/notifications"
                icon={Bell}
                label="Notifikasi"
                badge={badgeSummary.notifications?.total || 0}
                onClick={closeSidebar}
              />
              <MenuCategory icon={FileText} label="Manajemen Order" badge={badgeSummary.orders?.total || 0}>
                <SubMenuItem to="/admin/orders" icon={FileText} label="Orders" badge={badgeSummary.orders?.total || 0} onClick={closeSidebar} />
                {/* 27 Okt 2026 — Admin CRM yang dicentang Finance (izin finance.console)
                    dapat submenu Invoices. Tanpa centang: tidak tampil. */}
                {hasPermission('finance.console') && (
                  <SubMenuItem to="/admin/invoices" icon={Receipt} label="Invoices" onClick={closeSidebar} />
                )}
                <SubMenuItem to="/admin/customers/master" icon={Users} label="Data Customer" onClick={closeSidebar} />
                <SubMenuItem to="/admin/promo-programs" icon={Megaphone} label="Program Blast" onClick={closeSidebar} />
                <SubMenuItem to="/admin/blast-voucher" icon={Ticket} label="Blast Voucher" onClick={closeSidebar} />
                <SubMenuItem to="/admin/daftar-block" icon={Ban} label="Daftar Block" onClick={closeSidebar} />
                <SubMenuItem to="/admin/vouchers" icon={Ticket} label="Voucher" onClick={closeSidebar} />
                <SubMenuItem to="/admin/vaccine-data-list" icon={Syringe} label="List Data Vaksin" onClick={closeSidebar} />
                <SubMenuItem to="/admin/midtrans-payments" icon={CreditCard} label="Pembayaran Midtrans" onClick={closeSidebar} />
              </MenuCategory>
              <StandaloneMenuItem
                to="/admin/knowledge-center"
                icon={BookOpen}
                label="Pusat Informasi"
                badge={badgeSummary.informasi?.total || 0}
                onClick={closeSidebar}
              />
              <StandaloneMenuItem to="/admin/apa-yang-baru" icon={Sparkles} label="Apa yang Baru" badge={appUpdatesCount} onClick={closeSidebar} />
            </>
          ) : /* Finance Role - Extended Menu with Payment Management */
          isFinance ? (
            <>
              <StandaloneMenuItem to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" onClick={closeSidebar} />
              
              {/* Notifikasi - Finance notifications */}
              <StandaloneMenuItem 
                to="/admin/notifications" 
                icon={Bell} 
                label="Notifikasi" 
                badge={badgeSummary.notifications?.total || 0}
                onClick={closeSidebar} 
              />
              
              {/* Pusat Informasi - visible for Finance too */}
              <StandaloneMenuItem 
                to="/admin/knowledge-center" 
                icon={BookOpen} 
                label="Pusat Informasi" 
                badge={badgeSummary.informasi?.total || 0}
                onClick={closeSidebar} 
              />
              <StandaloneMenuItem to="/admin/apa-yang-baru" icon={Sparkles} label="Apa yang Baru" badge={appUpdatesCount} onClick={closeSidebar} />
              
              {/* Manajemen Order - Finance view */}
              <MenuCategory icon={FileText} label="Manajemen Order">
                <SubMenuItem to="/admin/invoices" icon={Receipt} label="Invoices" onClick={closeSidebar} />
                <SubMenuItem to="/admin/vouchers" icon={Ticket} label="Vouchers" onClick={closeSidebar} />
              </MenuCategory>
              
              {/* Pembayaran Supplier - Finance can manage payment */}
              <MenuCategory icon={Truck} label="Pembayaran Supplier">
                <SubMenuItem to="/admin/purchase-orders" icon={ShoppingCart} label="Daftar Surat Pesanan" onClick={closeSidebar} />
              </MenuCategory>
            </>
          ) : (
            <>
              {/* Iter184 (May 30, 2026): urutan menu khusus untuk Super Admin /
                  Admin / SPV diubah sesuai permintaan. Role lain (HRD, Marketing,
                  Gudang, Nakes, Master Admin diperlakukan sama dengan Super Admin
                  karena MA = variant Super Admin) tetap pakai urutan lama.
                  Semua item didefinisikan sekali lalu di-render dalam urutan
                  yang sesuai — RBAC guards per-item TIDAK berubah. */}
              {(() => {
                const useNewOrder = isUserSuperAdmin || isSupervisor || userRole === 'admin' || userRole === 'master_admin';

                const blkDashboard = (
                  <StandaloneMenuItem
                    key="dashboard"
                    to="/admin/dashboard"
                    icon={LayoutDashboard}
                    label="Dashboard"
                    onClick={closeSidebar}
                  />
                );

                const blkTugas = (isAdmin || userRole === 'hrd') && (
                  <StandaloneMenuItem
                    key="tugas"
                    to="/admin/tasks"
                    icon={adminTasksCount.critical > 0 ? AlertTriangle : ClipboardList}
                    label="Tugas"
                    badge={adminTasksCount.total}
                    onClick={closeSidebar}
                  />
                );

                const blkPapanOps = (isAdmin || userRole === 'hrd') && (
                  <StandaloneMenuItem
                    key="papan-ops"
                    to="/admin/papan-operasional"
                    icon={Megaphone}
                    label="Papan Operasional"
                    badge={papanOpsCount.mine_pending || 0}
                    onClick={closeSidebar}
                  />
                );

                const blkPusatInfo = (
                  <React.Fragment key="pusat-info">
                    <StandaloneMenuItem
                      to="/admin/knowledge-center"
                      icon={BookOpen}
                      label="Pusat Informasi"
                      badge={badgeSummary.informasi?.total || 0}
                      onClick={closeSidebar}
                    />
                    <StandaloneMenuItem to="/admin/apa-yang-baru" icon={Sparkles} label="Apa yang Baru" badge={appUpdatesCount} onClick={closeSidebar} />
                  </React.Fragment>
                );

                const blkOrder = (
                  <MenuCategory key="order" icon={FileText} label="Manajemen Order" badge={badgeSummary.orders?.total || 0}>
                    <SubMenuItem to="/admin/orders" icon={FileText} label="Orders" badge={badgeSummary.orders?.total || 0} onClick={handleOrdersClick} />
                    <SubMenuItem to="/admin/handling-jobs" icon={ClipboardCheck} label="Handling Job" onClick={closeSidebar} />
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/free-orders-report" icon={ClipboardCheck} label="Laporan Order Gratis" onClick={closeSidebar} />
                    )}
                    <SubMenuItem to="/admin/orders/new" icon={FileText} label="Buat Order" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/schedule" icon={Calendar} label="Jadwal" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/invoices" icon={Receipt} label="Invoices" onClick={closeSidebar} />
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/vaccine-schedule-master" icon={Syringe} label="Master Jadwal Vaksin" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/order-trash" icon={Trash2} label="Tempat Sampah Order" onClick={closeSidebar} />
                    )}
                  </MenuCategory>
                );

                // Okt 2026: menu customer dipisah dari Manajemen Order.
                const blkCustomer = (
                  <MenuCategory key="customer" icon={Users} label="Manajemen Customer">
                    <SubMenuItem to="/admin/customers/master" icon={Users} label="Data Customer" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/customers/credits" icon={Wallet} label="Saldo Kredit" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/promo-programs" icon={Megaphone} label="Program Blast" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/blast-voucher" icon={Ticket} label="Blast Voucher" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/daftar-block" icon={Ban} label="Daftar Block" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/vouchers" icon={Ticket} label="Vouchers" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/referrers" icon={Gift} label="Master Referrer" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/vaccine-data-list" icon={Syringe} label="List Data Vaksin" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/medical-records" icon={Stethoscope} label="Medical Record" onClick={closeSidebar} />
                  </MenuCategory>
                );

                const blkLogistik = (
                  <MenuCategory key="logistik" icon={Truck} label="Manajemen Logistik">
                    {canSeePurchaseOrder && (
                      <SubMenuItem to="/admin/purchase-orders" icon={ShoppingCart} label="Surat Pesanan" onClick={closeSidebar} />
                    )}
                    <SubMenuItem to="/admin/supply-requests" icon={Package} label="Pengiriman Barang" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/logistic-returns" icon={PackageX} label="Retur Barang" onClick={closeSidebar} />
                    {(isAdmin || isUserSuperAdmin) && (
                      <SubMenuItem to="/admin/kartu-stok-nakes" icon={Package} label="Kartu Stok Nakes" onClick={closeSidebar} />
                    )}
                  </MenuCategory>
                );

                const blkChat = (
                  <StandaloneMenuItem
                    key="chat"
                    to="/admin/all-chats"
                    icon={MessagesSquare}
                    label="Chat"
                    badge={totalChatBadge}
                    onClick={closeSidebar}
                  />
                );

                const blkMasterData = (
                  <MenuCategory key="master-data" icon={Database} label="Master Data">
                    <SubMenuItem to="/admin/master-products" icon={Package} label="Master Layanan" onClick={closeSidebar} />
                    {(isUserSuperAdmin || isSupervisor) && (
                      <SubMenuItem to="/admin/product-categories" icon={Tag} label="Kategori Produk" onClick={closeSidebar} />
                    )}
                    <SubMenuItem to="/admin/master-supplier" icon={Database} label="Master Supplier" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/master-supplier-product" icon={Database} label="Produk Supplier" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/master-vaxa-product" icon={Database} label="Produk Vaxa" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/konfirmasi-produk" icon={Database} label="Konfirmasi Produk Baru" badge={productConfirmCount} onClick={closeSidebar} />
                    <SubMenuItem to="/admin/master-inventory" icon={Pill} label="Master Barang" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/equipment-usage" icon={Package} label="Pemakaian Alat" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/stock-monitor/gudang" icon={Package} label="Stock Monitor Gudang" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/stock-monitor/nakes" icon={Package} label="Stock Monitor Nakes" onClick={closeSidebar} />
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/master-entities" icon={Building2} label="Master Entitas" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/bank-accounts" icon={Landmark} label="Rekening Bank" onClick={closeSidebar} />
                    )}
                  </MenuCategory>
                );

                const blkSDM = canSeeUserMgmt && (
                  <MenuCategory key="sdm" icon={UserCog} label="Manajemen SDM">
                    <SubMenuItem to="/admin/users" icon={Users} label="Users" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/privilege-requests" icon={Star} label="Privilege Requests" onClick={closeSidebar} />
                  </MenuCategory>
                );

                const blkNakes = isAdmin && (
                  <MenuCategory key="nakes" icon={Stethoscope} label="Manajemen Nakes">
                    {(isAdmin || isUserSuperAdmin) && (
                      <SubMenuItem to="/admin/direktori-nakes" icon={MapPin} label="Direktori Nakes" onClick={closeSidebar} />
                    )}
                    {canSeeUserMgmt && (
                      <SubMenuItem to="/admin/nurses" icon={Stethoscope} label="Nurses" onClick={closeSidebar} />
                    )}
                    {isAdmin && (
                      <SubMenuItem to="/admin/payroll" icon={Wallet} label="Payroll Nakes" onClick={closeSidebar} />
                    )}
                    {isAdmin && (
                      <SubMenuItem to="/admin/nakes/extra-transport-history" icon={Car} label="History ET & Nego Transport" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isSupervisor) && (
                      <SubMenuItem to="/admin/deposits" icon={Wallet} label="Deposit Nakes" onClick={closeSidebar} />
                    )}
                    {isAdmin && (
                      <SubMenuItem to="/admin/attendance" icon={ClipboardCheck} label="Kehadiran Nakes" onClick={closeSidebar} />
                    )}
                    {/* Oct 2 2026 — sebelumnya canSeeUserMgmt (hanya super admin + supervisor),
                        padahal backend stock_opname.manage sudah mengizinkan admin. Sekarang
                        semua admin bisa melihat dan membuat request stok opname. */}
                    {isAdmin && (
                      <SubMenuItem to="/admin/stok-opname" icon={ClipboardCheck} label="Stok Opname Nakes" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isAdmin) && (
                      <SubMenuItem to="/admin/nakes-performance/verifikasi-sop" icon={ClipboardCheck} label="Verifikasi SOP Nakes" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isSupervisor) && (
                      <SubMenuItem to="/admin/nakes-performance/sop-audit" icon={ClipboardCheck} label="Audit Verifikasi SOP" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isSupervisor) && (
                      <SubMenuItem to="/admin/nakes-performance/cek-barang-keluar" icon={PackageCheck} label="Cek Barang Keluar" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isAdmin) && (
                      <SubMenuItem to="/admin/nakes-performance/scorecard-monthly" icon={Calendar} label="Scorecard Bulanan" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isAdmin) && (
                      <SubMenuItem to="/admin/nakes-performance/scorecards" icon={BarChart3} label="Scorecard Legacy" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isSupervisor) && (
                      <SubMenuItem to="/admin/nakes-performance/kelas-nakes" icon={Award} label="Kelas Nakes" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isAdmin) && (
                      <SubMenuItem to="/admin/nakes-performance/complaints" icon={AlertCircle} label="Complaint & SP" onClick={closeSidebar} />
                    )}
                  </MenuCategory>
                );

                const blkReports = (isAdmin || isUserSuperAdmin) && (
                  <MenuCategory key="reports" icon={BarChart3} label="Laporan">
                    <SubMenuItem to="/admin/reports" icon={BarChart3} label="Laporan Order" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/reports/marketing-daily" icon={Activity} label="Marketing Daily Report" onClick={closeSidebar} />
                    {(isUserSuperAdmin || isSupervisor) && (
                      <SubMenuItem to="/admin/midtrans-payments" icon={CreditCard} label="Pembayaran Midtrans" onClick={closeSidebar} />
                    )}
                    {userRole === 'super_admin' && (
                      <SubMenuItem to="/admin/pancake-report" icon={MessageSquare} label="Laporan Chat Marketing" onClick={closeSidebar} />
                    )}
                    {(isUserSuperAdmin || isSupervisor) && (
                      <SubMenuItem to="/admin/nakes-performance/sop-audit-report" icon={ClipboardCheck} label="Laporan Audit SOP" onClick={closeSidebar} />
                    )}
                  </MenuCategory>
                );

                const blkMonitoring = isUserSuperAdmin && (
                  <MenuCategory
                    key="monitoring"
                    icon={Activity}
                    label="Monitoring"
                    badge={tgHealthFlags.issue_count}
                    title={tgHealthFlags.issue_count > 0 ? `${tgHealthFlags.issue_count} masalah Telegram terdeteksi — cek Telegram Monitor` : 'Monitoring'}
                  >
                    <SubMenuItem to="/admin/audit-log" icon={Activity} label="Activity Log" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/helpdesk/tickets" icon={Ticket} label="Support Tickets" onClick={closeSidebar} />
                    <SubMenuItem
                      to="/admin/telegram-monitor"
                      icon={Send}
                      label="Telegram Monitor"
                      badge={tgHealthFlags.issue_count}
                      onClick={closeSidebar}
                      title={tgHealthFlags.issue_count > 0
                        ? `${tgHealthFlags.unverified_channel_count} channel unverified${tgHealthFlags.super_admin_linked === 0 ? ' · Super Admin belum terlink' : ''}`
                        : 'Telegram Monitor'}
                    />
                    <SubMenuItem to="/admin/marketing-reminder-debug" icon={Bell} label="Marketing Reminder Debug" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/admin-reminder-debug" icon={Bell} label="Admin Reminder Debug" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/nakes-reminder-debug" icon={Bell} label="Nakes Reminder Debug" onClick={closeSidebar} />
                  </MenuCategory>
                );

                const blkPengaturan = (isUserSuperAdmin || isSupervisor) && (
                  <MenuCategory key="pengaturan" icon={Settings} label="Pengaturan">
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/document-settings" icon={FileEdit} label="Teks Dokumen" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/vouchers/templates" icon={Ticket} label="Template Pesan" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/payment-settings" icon={CreditCard} label="Metode Pembayaran" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/region-settings" icon={MapPin} label="Master Wilayah" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/message-templates" icon={MessageSquare} label="Message Templates" onClick={closeSidebar} />
                    )}
                    <SubMenuItem to="/admin/announcements" icon={Megaphone} label="Announcements" onClick={closeSidebar} />
                    <SubMenuItem to="/admin/roles" icon={Shield} label="Master Role" onClick={closeSidebar} />
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/telegram" icon={Send} label="Telegram" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/knowledge-base" icon={BookOpen} label="Knowledge Base AI" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/settings/tutorial-cms" icon={FileEdit} label="CMS Tutorial" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/settings/apa-yang-baru" icon={Sparkles} label="Kelola Apa yang Baru" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/settings/helpdesk-kb" icon={HelpCircle} label="Knowledge Base Helpdesk" onClick={closeSidebar} />
                    )}
                    {isUserSuperAdmin && (
                      <SubMenuItem to="/admin/settings/medicine-dictionary" icon={Pill} label="Kamus Obat" onClick={closeSidebar} />
                    )}
                  </MenuCategory>
                );

                // New order — applies to Super Admin / Admin / SPV / Master Admin
                const newOrder = [
                  blkDashboard,        // 1. Dashboard
                  blkTugas,            // 2. Tugas
                  blkPapanOps,         // 3. Papan Operasional
                  blkOrder,            // 4. Manajemen Order
                  blkCustomer,         // 4b. Manajemen Customer
                  blkNakes,            // 5. Manajemen Nakes
                  blkLogistik,         // 6. Manajemen Logistik
                  blkSDM,              // 7. Manajemen SDM
                  blkChat,             // 8. Chat
                  blkMasterData,       // 9. Master Data
                  blkPusatInfo,        // 10. Pusat Informasi
                  blkReports,          // 12. Reports
                  blkMonitoring,       // 13. Monitoring
                  blkPengaturan,       // 14. Pengaturan
                ];

                // Old order — preserved for other roles (HRD, Marketing, etc.)
                const oldOrder = [
                  blkDashboard,
                  blkTugas,
                  blkPapanOps,
                  blkPusatInfo,
                  blkOrder,
                  blkCustomer,
                  blkLogistik,
                  blkChat,
                  blkMasterData,
                  blkSDM,
                  blkNakes,
                  blkReports,
                  blkMonitoring,
                  blkPengaturan,
                ];

                return <>{(useNewOrder ? newOrder : oldOrder).filter(Boolean)}</>;
              })()}
            </>
          )}
        </nav>

        {/* Bottom: Profile row (with bell aligned right) + Logout row below */}
        <div className="flex-shrink-0 p-2 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          {/* Row 1: Profile (flex-1) + Bell pinned right — bell never overlaps text */}
          <div className="flex items-center gap-2 mb-2">
            <Link
              to="/admin/profile"
              data-testid="profile-link"
              className="flex items-center gap-2 flex-1 min-w-0 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={closeSidebar}
            >
              <User className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate leading-tight">{user?.name}</p>
                <p className="text-[10px] text-gray-500 truncate leading-tight">{user?.user_id} · {getRoleLabel()}</p>
              </div>
            </Link>

            <Link
              to="/admin/notifications"
              onClick={closeSidebar}
              aria-label="Notifikasi"
              className="relative flex items-center justify-center w-11 h-11 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              data-testid="sidebar-notification-bell"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              {(badgeSummary.notifications?.total || 0) > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center px-1 rounded-full">
                  {(badgeSummary.notifications.total > 99) ? '99+' : badgeSummary.notifications.total}
                </span>
              )}
            </Link>
          </div>

          {/* Row 2: Logout — full width, function unchanged */}
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>

          {/* Build Version Label */}
          <div
            className="mt-1 text-center text-[9px] text-gray-400 font-mono cursor-pointer hover:text-gray-600"
            onClick={() => {
              const buildInfo = `Build: b5c81558\nDate: 2026-03-04T08:26:09Z\nFeatures: vaccination-docs-v2`;
              navigator.clipboard?.writeText(buildInfo);
            }}
            title="Click to copy build info"
            data-testid="build-version"
          >
            v.b5c81558
          </div>
        </div>
      </div>

      {/* Main Content - Scrollable area, offset by sidebar width on desktop */}
      <div className="flex-1 min-w-0 min-h-screen lg:ml-64 lg:h-screen lg:overflow-y-auto">
        {/* Mobile Sticky Header — Feb 2026 (v2 redesign).
            iter386: Skip rendering when user is inside a chat room so the
            room's own <ChatHeaderMobile> isn't covered by this 56px navbar.
            iter388: Also skip when a thread is open inside /all-chats. */}
        {!hideNavbar && (
          <MobileStickyHeader
            userName={user?.name || 'User'}
            userRole={getRoleLabel()}
            sidebarOpen={sidebarOpen}
            onMenuClick={() => setSidebarOpen(!sidebarOpen)}
            offsetTop={bannerHeight}
          />
        )}
        <PendingNakesRequestBanner
          onHeightChange={setBannerHeight}
          fixedMobile={!hideNavbar}
        />
        <PendingOTWCancelBanner
          onHeightChange={setBannerHeight}
          fixedMobile={!hideNavbar}
        />
        {/* Global Content Wrapper - Consistent padding and max-width */}
        <div
          className={`w-full h-full ${hideNavbar ? '' : 'pt-[calc(3.5rem+var(--banner-h,0px))] lg:pt-0 pb-20 lg:pb-0'}`}
          style={hideNavbar ? undefined : { '--banner-h': `${bannerHeight}px` }}
        >
          <PullToRefresh>
            <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
              <Outlet />
            </div>
          </PullToRefresh>
        </div>
      </div>
    </div>
    </SidebarAccordionContext.Provider>
  );
}
